import { CommonModule } from '@angular/common';
import { Component, Inject, inject, DestroyRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TsButtonModule } from 'tsui';
import { AccountService } from 'app/core';
import { TaskListTemplate } from '@shared/modules/legacy-tasks/tasks.types';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import * as CustomValidator from '@shared/directives/custom-validators.directive';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';

const MATERIAL_MODULES = [MatFormFieldModule, MatInputModule, MatSelectModule, MatTooltipModule];

@Component({
  selector: 'settings-task-template-create-template-dialog',
  templateUrl: './create-task-list-template-dialog.component.html',
  styles: [
    `
      ::ng-deep .mat-form-field-wrapper {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ...MATERIAL_MODULES, TsButtonModule],
})
export class SettingsTaskTemplatesCreateTemplateDialog {
  public isLoading: boolean = false;
  public createTaskListTemplateForm: UntypedFormGroup;
  public applicableTypes = [
    {
      value: 'Project',
      label: 'Deal',
    },
    {
      value: 'Property',
      label: 'Property',
    },
    {
      value: 'Account',
      label: 'Global',
    },
  ];

  get dynamicDialogTitle(): string {
    return this.data.saveAsTemplate ? 'Save as template' : 'Create new template';
  }

  private get accountId(): number {
    return Number(this.accountService.getCurrentAccount().id);
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      taskListId?: number;
      saveAsTemplate?: boolean;
      createNewTemplate?: boolean;
    },
    private readonly matDialogRef: MatDialogRef<SettingsTaskTemplatesCreateTemplateDialog>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly tasksService: LegacyTasksTabService,
    private readonly accountService: AccountService,
    private readonly objectsManagerService: ObjectsManagerService,
  ) {}

  ngOnInit(): void {
    this.matDialogRef.updateSize('50vw', 'auto');
    this.buildForm();
    this.getCustomObjects();
  }

  private buildForm(): void {
    this.createTaskListTemplateForm = this.formBuilder.group({
      name: ['', [Validators.required, CustomValidator.noWhiteValidator]],
      applicableType: ['', [Validators.required, CustomValidator.noWhiteValidator]],
    });
  }

  private getCustomObjects(): void {
    this.objectsManagerService
      .get(this.accountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((objects) => {
        let customObjects = objects.filter((object) => object?.id);
        for (let customObject of customObjects) {
          this.applicableTypes.push({
            value: customObject.name,
            label: customObject.label,
          });
        }
      });
  }

  hasRequiredFieldError(fieldControlName: string): boolean {
    return (
      this.createTaskListTemplateForm.get(fieldControlName).touched &&
      this.createTaskListTemplateForm.get(fieldControlName).errors?.required
    );
  }

  public discard(): void {
    this.matDialogRef.close();
  }

  public save(): void {
    this.isLoading = true;
    this.createTaskListTemplateForm.disable();
    const formValue = this.createTaskListTemplateForm.value;
    let taskListTemplate: TaskListTemplate = { ...formValue, applicable_type: formValue.applicableType };
    if (this.data.saveAsTemplate) taskListTemplate.copy_from_task_list_id = this.data.taskListId;

    this.tasksService
      .createTaskListTemplate(taskListTemplate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((taskListTemplate) => {
        this.matDialogRef.close(taskListTemplate);
      });
  }
}
